import {
  Flex,
  OutsideClicker,
  useStyledTheme,
} from '@equifinance/shared';
import styled from 'styled-components';
import { useRef, useState } from 'react';
import { DocumentStatus } from '../../../../contexts/tasks';

const SelectStatusDropdown = styled.div`${({ theme }) => `
  position: absolute;
  z-index: 2;
  overflow-x: unset !important;
  box-shadow: 0px 0px 4px rgba(0, 0, 0, 0.16);
  padding: ${theme.padding[1]} 0;
  margin-top: ${theme.padding[1]};
  background-color: ${theme.colours.neutral[10]};
  border-radius: ${theme.borderRadius[1]};
  color: ${theme.colours.neutral[1]};
  font-size: ${theme.fontSize[1]};
  font-weight: ${theme.fontWeight.normal};
  width: ${theme.taskEvidenceStatusDropdownWidth};
`}`;

const StatusDropdownItem = styled.div<{ checked?: boolean }>`${({ theme, checked }) => `
  padding: ${theme.padding[2]} ${theme.padding[4]};
  line-height: ${theme.fontSize[3]};
  font-size: ${theme.fontSize[1]};

  &:hover {
    cursor: pointer;
    font-weight: ${theme.fontWeight.bold};
    background-color: ${theme.colours.neutral[9]};
  };

  ${checked && `
    background-color: ${theme.colours.neutral[8]};
  `};

  white-space: normal;
`}`;

const DropdownIcon = styled.i` ${({ theme }) => `
  color: ${theme.colours.neutral[2]};
  font-size: ${theme.fontSize[3]};
  width: ${theme.fontSize[3]};
  height: ${theme.fontSize[6]};
`}`;

const DocumentaryEvidenceStatusContainer = styled.div`
  position: relative;
`;

const DocumentaryEvidenceStatusDropdown = styled(Flex) <{ bgColor?: string }>`${({ theme, bgColor }) => `
  justify-content: flex-start;
  align-items: center;
  padding: ${theme.padding[5]} ${theme.padding[3]};
  background: ${bgColor};
  cursor: pointer;
  width: ${theme.taskEvidenceStatusWidth};
  gap: ${theme.padding[1]};
`}`;

const DocumentStatusLabel = styled.span`${({ theme }) => `
  font-weight: ${theme.fontWeight.bold};
  font-size: ${theme.fontSize[1]};
  text-align: left;
`}`;

const Icon = styled.i<{ color?: string }>` ${({ theme, color }) => `
  color: ${color};
  font-size: ${theme.fontSize[5]};
`}`;

function useBgColor(status: keyof typeof DocumentStatus): string | undefined {
  const { colours } = useStyledTheme();

  switch (status) {
    case 'Approved': return colours.success[2];
    case 'ApprovedSubjectToExpiry': return colours.success[2];
    case 'Uploaded': return colours.primary[10];
    case 'Deleted': return colours.neutral[8];
    case 'SubmittedToLender': return colours.highlight[2];
    case 'WithThirdParty': return colours.primary[11];
    case 'Rejected': return colours.danger[2];
    case 'Expired': return colours.danger[2];
    case 'ToExpire': return colours.secondary[7];
    case 'UnderwriterDocument': return colours.warning[4];
    default: return colours.neutral[8];
  }
}

function DocumentStatusIcon({ status }: { status: keyof typeof DocumentStatus }) {
  const { colours } = useStyledTheme();

  switch (status) {
    case 'Approved':
    case 'ApprovedSubjectToExpiry': return <Icon className="mi-circle-check" color={colours.success[1]} />;
    case 'Uploaded': return <Icon className="mi-document-check" color={colours.info[1]} />;
    case 'Deleted': return <Icon className="mi-ban" color={colours.neutral[4]} />;
    case 'SubmittedToLender': return <Icon className="mi-clock" color={colours.secondary[1]} />;
    case 'WithThirdParty': return <Icon className="mi-clock" color={colours.info[1]} />;
    case 'Rejected': return <Icon className="mi-circle-error" color={colours.danger[1]} />;
    case 'Expired': return <Icon className="mi-warning" color={colours.danger[1]} />;
    case 'ToExpire': return <Icon className="mi-warning" color={colours.secondary[1]} />;
    case 'UnderwriterDocument': return <></>;
    default: return <></>;
  }
}

const DocumentStatusOptions = {
  Approved: 'Approved',
  ApprovedSubjectToExpiry: 'Approved (subject to expiry)',
  Uploaded: 'Uploaded',
  SubmittedToLender: 'Submitted To Lender',
  WithThirdParty: 'With Third Party',
  Rejected: 'Rejected',
  Expired: 'Expired',
  ToExpire: 'To Expire',
  UnderwriterDocument: 'UW Document',
};

export function DocumentaryEvidenceStatus({ status, onSelect = () => { }, readOnly = true }: { status: keyof typeof DocumentStatus, onSelect?: (v: string) => void, readOnly?: boolean }) {
  const bgStatusColor = useBgColor(status);
  const ref = useRef<HTMLDivElement>(null);

  const [isOpen, setIsOpen] = useState(false);
  const documentStatus = DocumentStatus[status];
  const statusOptions = Object.entries(DocumentStatusOptions);

  const toggleDropdown = () => {
    setIsOpen((prev) => !prev);
  };

  const onClickOutside = () => {
    setIsOpen(false);
  };

  const onSelectStatus = (targetValue: string) => {
    onSelect(targetValue);
    setIsOpen(false);
  };

  const checkSelected = (optionValue: string) => (optionValue === status);

  return (
    <DocumentaryEvidenceStatusContainer>
      <DocumentaryEvidenceStatusDropdown bgColor={bgStatusColor} onClick={toggleDropdown} ref={ref}>
        <DocumentStatusIcon status={status} />
        <DocumentStatusLabel>{documentStatus}</DocumentStatusLabel>
        {!readOnly && <DropdownIcon className={`${isOpen ? 'mi-chevron-up' : 'mi-chevron-down'}`} />}
      </DocumentaryEvidenceStatusDropdown>

      {(isOpen && !readOnly) && (
        <OutsideClicker
          onClickOutside={onClickOutside}
          ignoredEffectElements={[ref.current]}
        >
          <SelectStatusDropdown>
            {statusOptions.map(
              ([key, optionValue]) => (
                <StatusDropdownItem
                  key={key}
                  onClick={() => onSelectStatus(key)}
                  checked={checkSelected(key)}
                >
                  {optionValue}
                </StatusDropdownItem>
              ),
            )}
          </SelectStatusDropdown>
        </OutsideClicker>
      )}
    </DocumentaryEvidenceStatusContainer>
  );
}
